import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, MessageCircle, RefreshCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ErrorState, LoadingState, PageHeading } from "@/components/app/AppUI";
import { fechaHora } from "@/components/crm/leads/formato";
import { whatsappRepository } from "@/data/repositories";
import type { WhatsappEstado } from "@/domain/types";
import type { ConfiguracionWhatsapp } from "@/server/whatsapp.functions";
import type { PasoConexion, ResultadoConexion } from "@/whatsapp/conexion";

export const Route = createFileRoute("/_authenticated/app/whatsapp")({
  component: PaginaWhatsapp,
});

/* --- SDK de Facebook (registro insertado de Meta) --------------------------
 * https://developers.facebook.com/docs/whatsapp/embedded-signup/implementation
 * https://developers.facebook.com/docs/whatsapp/embedded-signup/custom-flows/onboarding-business-app-users
 * La política de seguridad solo deja cargarlo en esta página (src/server.ts),
 * y por eso el menú la abre recargando: si se llega navegando dentro del CRM,
 * la página anterior no lo permite y hay que recargar.
 */

interface RespuestaLogin {
  authResponse?: { code?: string } | null;
  status?: string;
}

interface SdkFacebook {
  init(opciones: Record<string, unknown>): void;
  login(callback: (respuesta: RespuestaLogin) => void, opciones: Record<string, unknown>): void;
}

declare global {
  interface Window {
    FB?: SdkFacebook;
    fbAsyncInit?: () => void;
  }
}

let sdkCargando: Promise<SdkFacebook> | null = null;

function cargarSdk(appId: string, version: string): Promise<SdkFacebook> {
  sdkCargando ??= new Promise<SdkFacebook>((resolver, rechazar) => {
    window.fbAsyncInit = () => {
      window.FB!.init({ appId, autoLogAppEvents: true, xfbml: false, version });
      resolver(window.FB!);
    };
    const script = document.createElement("script");
    script.src = "https://connect.facebook.net/es_ES/sdk.js";
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.onerror = () => {
      sdkCargando = null;
      rechazar(new Error("sdk-bloqueado"));
    };
    document.body.appendChild(script);
  });
  return sdkCargando;
}

/** Lo que manda la ventana de Meta al terminar: la cuenta (WABA) y el número. */
interface DatosRegistro {
  wabaId: string | null;
  phoneNumberId: string | null;
}

function esDeFacebook(origen: string): boolean {
  try {
    const host = new URL(origen).hostname;
    return host === "facebook.com" || host.endsWith(".facebook.com");
  } catch {
    return false;
  }
}

/* --- Página ---------------------------------------------------------------- */

function PaginaWhatsapp() {
  const estado = useQuery({
    queryKey: ["whatsapp-estado"],
    queryFn: () => whatsappRepository.estado(),
    refetchInterval: 30_000,
  });
  const configuracion = useQuery({
    queryKey: ["whatsapp-configuracion"],
    queryFn: () => whatsappRepository.configuracion(),
  });

  if (estado.isLoading || configuracion.isLoading) return <LoadingState />;
  if (estado.error || configuracion.error || !estado.data || !configuracion.data)
    return (
      <ErrorState
        message={estado.error?.message ?? configuracion.error?.message ?? "Sin datos de WhatsApp"}
      />
    );

  return (
    <div className="space-y-8">
      <PageHeading
        eyebrow="Ventas"
        title="WhatsApp"
        description="Los mensajes de tu WhatsApp Business llegan solos a la ficha de cada pareja. Tú sigues usando la app del móvil como siempre."
      />
      <Estado estado={estado.data} />
      <Conectar estado={estado.data} configuracion={configuracion.data} />
      <Avisos />
    </div>
  );
}

function Fila({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div>
      <p className="eyebrow">{etiqueta}</p>
      <p className="mt-1">{valor}</p>
    </div>
  );
}

const cuando = (iso?: string | null) => (iso ? fechaHora.format(new Date(iso)) : null);

function textoHistorial(e: WhatsappEstado): string {
  if (e.historial_error) return `No llegó: ${e.historial_error}`;
  if (e.historial_completado_at) return `Completo (${cuando(e.historial_completado_at)})`;
  if (e.historial_progreso !== null && e.historial_progreso !== undefined)
    return `Llegando: ${e.historial_progreso} %`;
  if (e.historial_pedido_at) return `Pedido el ${cuando(e.historial_pedido_at)}; aún no ha llegado`;
  return "Sin pedir";
}

function Estado({ estado }: { estado: WhatsappEstado }) {
  if (!estado.conectado && !estado.desconectado_at)
    return (
      <section className="border border-border bg-white p-5">
        <p className="flex items-center gap-2 font-semibold">
          <MessageCircle className="size-4 text-muted-foreground" /> Sin conectar
        </p>
        <p className="mt-1 text-muted-foreground">Todavía no llega nada de WhatsApp al CRM.</p>
      </section>
    );

  return (
    <section className="grid gap-5 border border-border bg-white p-5">
      <p className="flex items-center gap-2 font-semibold">
        {estado.conectado ? (
          <>
            <CheckCircle2 className="size-4 text-emerald-700" /> Conectado
          </>
        ) : (
          <>
            <AlertTriangle className="size-4 text-red-700" /> Desconectado el{" "}
            {cuando(estado.desconectado_at)}
          </>
        )}
      </p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Fila
          etiqueta="Número"
          valor={[estado.numero, estado.nombre].filter(Boolean).join(" · ") || "—"}
        />
        <Fila etiqueta="Conectado desde" valor={cuando(estado.conectado_at) ?? "—"} />
        <Fila
          etiqueta="Avisos de Meta"
          valor={
            estado.suscrito_at
              ? `Activados · último ${cuando(estado.ultimo_webhook_at) ?? "aún ninguno"}`
              : "Sin activar"
          }
        />
        <Fila etiqueta="Historial (180 días)" valor={textoHistorial(estado)} />
        <Fila
          etiqueta="Contactos"
          valor={
            estado.contactos_pedido_at
              ? `Pedidos el ${cuando(estado.contactos_pedido_at)}`
              : "Sin pedir"
          }
        />
        <Fila
          etiqueta="Mensajes guardados"
          valor={`${estado.mensajes ?? 0}${
            estado.mensajes_sin_lead
              ? ` (${estado.mensajes_sin_lead} de números que no son leads)`
              : ""
          }`}
        />
      </div>
      {estado.ultimo_error && (
        <p className="border-l-2 border-red-700 pl-3 text-sm text-red-800">{estado.ultimo_error}</p>
      )}
    </section>
  );
}

function Conectar({
  estado,
  configuracion,
}: {
  estado: WhatsappEstado;
  configuracion: ConfiguracionWhatsapp;
}) {
  const cliente = useQueryClient();
  const registro = useRef<DatosRegistro | null>(null);
  const [sdkBloqueado, setSdkBloqueado] = useState(false);
  const [pasos, setPasos] = useState<PasoConexion[] | null>(null);
  const [esperando, setEsperando] = useState(false);

  // La ventana de Meta avisa con un mensaje al terminar (o al cancelar).
  useEffect(() => {
    function alMensaje(evento: MessageEvent) {
      if (!esDeFacebook(evento.origin) || typeof evento.data !== "string") return;
      try {
        const datos = JSON.parse(evento.data) as {
          type?: string;
          event?: string;
          data?: { waba_id?: string; phone_number_id?: string; current_step?: string };
        };
        if (datos.type !== "WA_EMBEDDED_SIGNUP") return;
        if (datos.event === "CANCEL") {
          setEsperando(false);
          toast.message("Conexión cancelada", {
            description: datos.data?.current_step
              ? `En el paso: ${datos.data.current_step}`
              : undefined,
          });
          return;
        }
        if (datos.event?.startsWith("FINISH"))
          registro.current = {
            wabaId: datos.data?.waba_id ?? null,
            phoneNumberId: datos.data?.phone_number_id ?? null,
          };
      } catch {
        // Otros mensajes del SDK que no son JSON.
      }
    }
    window.addEventListener("message", alMensaje);
    return () => window.removeEventListener("message", alMensaje);
  }, []);

  // Precarga el SDK para que el botón abra la ventana sin esperas (si no, el
  // navegador puede bloquearla).
  useEffect(() => {
    if (!configuracion.appId) return;
    cargarSdk(configuracion.appId, configuracion.versionGraph).catch(() => setSdkBloqueado(true));
  }, [configuracion.appId, configuracion.versionGraph]);

  const tras = (resultado: ResultadoConexion) => {
    setPasos(resultado.pasos);
    void cliente.invalidateQueries({ queryKey: ["whatsapp-estado"] });
    if (resultado.pasos.every((p) => p.ok))
      toast.success("WhatsApp conectado. El historial irá llegando en los próximos minutos.");
    else toast.error("Conectado, pero algún paso ha fallado. Mira abajo.");
  };

  const conectar = useMutation({
    mutationFn: async (codigo: string) => {
      // El código caduca a los 30 s; el mensaje con la cuenta suele llegar antes.
      for (let i = 0; i < 20 && !registro.current; i++)
        await new Promise((r) => setTimeout(r, 200));
      return whatsappRepository.conectar({
        codigo,
        wabaId: registro.current?.wabaId ?? null,
        phoneNumberId: registro.current?.phoneNumberId ?? null,
      });
    },
    onSuccess: tras,
    onError: (error) => toast.error(error.message),
    onSettled: () => setEsperando(false),
  });

  const reintentar = useMutation({
    mutationFn: () => whatsappRepository.reintentar(),
    onSuccess: tras,
    onError: (error) => toast.error(error.message),
  });

  function lanzar() {
    const fb = window.FB;
    if (!fb || !configuracion.configId) return;
    registro.current = null;
    setPasos(null);
    setEsperando(true);
    // Sin async: el SDK de Facebook no acepta callbacks asíncronos.
    fb.login(
      (respuesta) => {
        const codigo = respuesta.authResponse?.code;
        if (codigo) conectar.mutate(codigo);
        else setEsperando(false);
      },
      {
        config_id: configuracion.configId,
        response_type: "code",
        override_default_response_type: true,
        // Coexistencia: conectar el número que ya está en la app WhatsApp Business.
        extras: { setup: {}, featureType: "whatsapp_business_app_onboarding" },
      },
    );
  }

  if (configuracion.faltan.length > 0)
    return (
      <section className="border border-amber-300 bg-amber-50 p-5 text-sm">
        <p className="font-semibold">Falta configurar la app de Meta en el servidor</p>
        <p className="mt-1">Variables sin poner: {configuracion.faltan.join(", ")}.</p>
        <p className="mt-1 text-muted-foreground">
          Los pasos están en herramientas/asistente-movil/WHATSAPP.md.
        </p>
      </section>
    );

  const pendientes =
    estado.conectado &&
    (!estado.suscrito_at || !estado.historial_pedido_at || !estado.contactos_pedido_at);

  return (
    <section className="grid gap-4">
      {!estado.conectado && (
        <div className="border-l-2 border-amber-500 bg-amber-50 p-4 text-sm">
          <p className="font-semibold">Antes de empezar</p>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            <li>
              Ten a mano el móvil con WhatsApp Business actualizado: Meta te enseñará un código QR
              que tienes que escanear desde la app.
            </li>
            <li>
              Cuando te pregunte por el número, elige{" "}
              <strong>conectar tu app WhatsApp Business</strong> existente.{" "}
              <strong>No registres el número</strong> como nuevo: lo quitaría de la app del móvil.
            </li>
            <li>En la app, acepta compartir el historial de chats.</li>
          </ul>
        </div>
      )}

      {sdkBloqueado ? (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <p>Para conectar hay que recargar esta página.</p>
          <button type="button" className="btn-secondary" onClick={() => window.location.reload()}>
            <RefreshCw className="size-4" /> Recargar
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            className={estado.conectado ? "btn-quiet" : "btn-primary"}
            disabled={esperando || conectar.isPending}
            onClick={lanzar}
          >
            <MessageCircle className="size-4" />
            {conectar.isPending
              ? "Conectando…"
              : esperando
                ? "Esperando a Meta…"
                : estado.conectado
                  ? "Volver a conectar"
                  : "Conectar WhatsApp"}
          </button>
          {pendientes && (
            <button
              type="button"
              className="btn-secondary"
              disabled={reintentar.isPending}
              onClick={() => reintentar.mutate()}
            >
              <RefreshCw className="size-4" />{" "}
              {reintentar.isPending ? "Reintentando…" : "Reintentar lo que falta"}
            </button>
          )}
        </div>
      )}

      {pasos && (
        <ul className="text-sm">
          {pasos.map((p) => (
            <li key={p.paso} className={p.ok ? "text-emerald-800" : "text-red-800"}>
              {p.ok ? "✓" : "✗"} {ETIQUETA_PASO[p.paso]}
              {p.error ? `: ${p.error}` : ""}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const ETIQUETA_PASO: Record<PasoConexion["paso"], string> = {
  suscribir: "Activar los avisos de Meta",
  contactos: "Pedir los contactos",
  historial: "Pedir el historial de 180 días",
};

function Avisos() {
  return (
    <section className="grid gap-2 text-sm text-muted-foreground">
      <p className="eyebrow">Para que siga funcionando</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          Abre WhatsApp Business en el móvil al menos una vez por semana. Si pasan unos 14 días sin
          abrirla, Meta desconecta la cuenta.
        </li>
        <li>
          Lo que contestes desde WhatsApp para Windows no llega al CRM (Meta no lo admite). Desde el
          móvil o WhatsApp Web, sí.
        </li>
        <li>El historial solo se puede pedir una vez y en las 24 horas siguientes a conectar.</li>
      </ul>
    </section>
  );
}
